import { idToken } from '../firebase';
import type { Deal, PublicPost } from '../data/types';
import type { Storyboard } from './storyboard';

const BACKEND = ((import.meta.env.VITE_BACKEND_URL as string | undefined) || '').replace(/\/$/, '');

export interface PublicChallenge {
  code: string; challengeId: string; deal: Deal; fromName: string; toName?: string;
  fromPost: PublicPost | null; toPost: PublicPost | null; completed: boolean; title: string; minutes: number;
  /** A Story About You Two: the crew's storyboard of each side, once drawn */
  fromStory?: Storyboard; toStory?: Storyboard;
  /** a guessing game: there is something to guess, and the answer is not here */
  hasAnswer?: boolean;
  guesses?: { text: string; correct: boolean; at: number; byName?: string }[];
}

/**
 * The server's own words where it has any.
 *
 * Every failure here used to collapse into one toast — "Could not make the
 * challenge" — which is a button that does not work as far as anybody using it
 * is concerned (#98). The backend says what was wrong; this carries it up so a
 * screen can repeat it.
 */
async function reason(res: Response, fallback: string): Promise<Error> {
  const err = new Error(fallback) as Error & { status?: number; reason?: string };
  err.status = res.status;
  try {
    const body = await res.json();
    if (body && typeof body.error === 'string') { err.reason = body.error; err.message = body.error; }
  } catch { /* not json; the fallback stands */ }
  return err;
}

/** A challenge sends a creative task and shows your work; the link opens the same exercise with the same deal. */
export async function createChallenge(input: { challengeId: string; deal: Deal; fromPostId: string; fromName?: string; toName?: string; answer?: string }): Promise<{ code: string; url: string; shareUrl: string }> {
  const token = await idToken();
  const res = await fetch(`${BACKEND}/api/kindling/challenges`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
  if (!res.ok) throw await reason(res, 'challenge failed');
  return res.json();
}

export async function fetchChallenge(code: string): Promise<PublicChallenge | null> {
  const res = await fetch(`${BACKEND}/api/kindling/challenges/${encodeURIComponent(code)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`fetch challenge failed: ${res.status}`);
  return res.json();
}

/** The friend finished: both posts on both feeds, and the two people are friends. */
export async function completeChallenge(code: string, toPostId: string, toName?: string): Promise<{ friendUid: string; fromName: string }> {
  const token = await idToken();
  const res = await fetch(`${BACKEND}/api/kindling/challenges/${encodeURIComponent(code)}/complete`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ toPostId, toName }) });
  if (!res.ok) throw new Error(`complete failed: ${res.status}`);
  return res.json();
}

/** A plain invite: friends without a task. */
export async function createInvite(fromName?: string): Promise<{ code: string; url: string; shareUrl: string }> {
  const token = await idToken();
  const res = await fetch(`${BACKEND}/api/kindling/invites`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ fromName }) });
  if (!res.ok) throw new Error(`invite failed: ${res.status}`);
  return res.json();
}

export async function fetchInvite(code: string): Promise<{ code: string; fromName: string } | null> {
  const res = await fetch(`${BACKEND}/api/kindling/invites/${encodeURIComponent(code)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`fetch invite failed: ${res.status}`);
  return res.json();
}

export async function acceptInvite(code: string, name?: string): Promise<{ friendUid: string; fromName: string }> {
  const token = await idToken();
  const res = await fetch(`${BACKEND}/api/kindling/invites/${encodeURIComponent(code)}/accept`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
  if (!res.ok) throw new Error(`accept failed: ${res.status}`);
  return res.json();
}

/**
 * Guess a guessing game. The answer comes back in the reply and nowhere else,
 * which is what the card has always promised (#96). Nothing is scored.
 */
export async function submitGuess(code: string, guess: string, byName?: string): Promise<{ correct: boolean; answer: string }> {
  const res = await fetch(`${BACKEND}/api/kindling/challenges/${encodeURIComponent(code)}/guess`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ guess, byName }) });
  if (!res.ok) throw await reason(res, 'could not send that guess');
  return res.json();
}
